import React, { useState } from 'react';
import TermlyFeeSummary from './TermlyFeeSummary';
import FeeBreakdownList from './FeeBreakdownList';
import CustomPaymentModal from './CustomPaymentModal';
import PaymentButton from './PaymentButton';
import { toast } from 'sonner';
import { CaretDown } from '@phosphor-icons/react';
import './TermlyFeeCard.css';

function TermlyFeeCard({ 
  childFees, 
  selectedChild, 
  selectedAcademicTerm,
  academicTerms,
  onPaymentSuccess,
  onTermChange
}) {
  const [showDetailBreakdown, setShowDetailBreakdown] = useState(false);
  const [selectedFeeForPayment, setSelectedFeeForPayment] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [selectedFeeForCustomAmount, setSelectedFeeForCustomAmount] = useState(null);

  if (!childFees) {
    return (
      <div className="termly-fee-card loading-state">
        <div className="spinner-pulse"></div>
        <p>Loading fees...</p>
      </div>
    );
  }

  // Get term name for display
  const termName = academicTerms?.find(t => t.id === selectedAcademicTerm)
    ? `${academicTerms.find(t => t.id === selectedAcademicTerm).term} - ${academicTerms.find(t => t.id === selectedAcademicTerm).academic_year}`
    : 'Current Term';

  const handleCustomAmountClick = (fee) => {
    setSelectedFeeForCustomAmount(fee);
    setShowCustomModal(true);
  };

  const handleCustomPaymentConfirm = async (amount) => {
    if (amount <= 0) {
      toast.error('Invalid amount');
      return;
    }

    if (amount > selectedFeeForCustomAmount.balance) {
      toast.error('Amount exceeds outstanding balance');
      return;
    }

    const customFee = {
      ...selectedFeeForCustomAmount,
      amount_to_pay: amount,
      fee_name: selectedFeeForCustomAmount.fee_name + ' (Partial)'
    };

    setSelectedFeeForPayment(customFee);
    setShowCustomModal(false);
    setSelectedFeeForCustomAmount(null);
  };

  const handlePaymentSuccess = () => {
    setSelectedFeeForPayment(null);
    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
    toast.success('Payment processed successfully!');
  };

  // Show payment button for selected fee
  if (selectedFeeForPayment) {
    return (
      <div className="termly-fee-card">
        <div className="payment-processing-view">
          <div className="processing-header">
            <button 
              className="back-btn"
              onClick={() => setSelectedFeeForPayment(null)}
            >
              ← Back to Fees
            </button>
            <h3>Complete Payment</h3>
            <div></div>
          </div>
          
          <PaymentButton 
            fee={selectedFeeForPayment}
            onPaymentSuccess={handlePaymentSuccess}
            fullWidth={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="termly-fee-card">
      {/* Term Filter */}
      {academicTerms && academicTerms.length > 0 && (
        <div className="term-filter-section">
          <label htmlFor="term-select">Select Academic Term</label>
          <select 
            id="term-select"
            value={selectedAcademicTerm || ''} 
            onChange={(e) => onTermChange(e.target.value)}
            className="term-select"
            data-testid="termly-fee-term-select"
          >
            <option value="">Choose a term...</option>
            {academicTerms.map(term => (
              <option key={term.id} value={term.id}>
                {term.term + ' - ' + term.academic_year}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Summary Section */}
      <TermlyFeeSummary 
        summary={childFees.summary}
        term={termName}
      />

      {/* Fee Breakdown Section */}
      <div className="breakdown-section">
        <button
          className={`breakdown-toggle ${showDetailBreakdown ? 'expanded' : ''}`}
          onClick={() => setShowDetailBreakdown(!showDetailBreakdown)}
          data-testid="toggle-fee-breakdown-btn"
        >
          <div className="toggle-left">
            <CaretDown size={20} />
            <span>Fee Details</span>
          </div>
          <span className="fee-count">({childFees.fees.length})</span>
        </button>

        {showDetailBreakdown && (
          <FeeBreakdownList 
            fees={childFees.fees}
            onCustomAmountClick={handleCustomAmountClick}
            isCompact={true}
          />
        )}
      </div>

      <CustomPaymentModal
        isOpen={showCustomModal}
        onClose={() => {
          setShowCustomModal(false);
          setSelectedFeeForCustomAmount(null);
        }}
        maxAmount={selectedFeeForCustomAmount?.balance || 0}
        defaultAmount={selectedFeeForCustomAmount?.balance || 0}
        onConfirm={handleCustomPaymentConfirm}
        title={`Enter Payment Amount for ${selectedFeeForCustomAmount?.fee_name}`}
      />
    </div>
  );
}

export default TermlyFeeCard;
